import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MagnifyingGlass, Star } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import communicationService from '../../services/communicationService';
import parentService from '../../services/parentService';
import './TeacherSelector.css';

/**
 * ParentTeacherSelector Component - Professional Redesign
 * 
 * Two-step selection process with improved UX:
 * 1. Parent selects their child
 * 2. System fetches teachers linked to that child
 * 3. Parent selects teacher to message
 * 
 * Features:
 * - Step indicator showing progress
 * - Recently messaged teachers quick access
 * - Improved search UI with icon
 * - Contact information display
 * - Better visual hierarchy and spacing
 * - Smooth animations and transitions
 * 
 * @param {string} selectedChild - Currently selected child ID
 * @param {function} onChildChange - Callback when child selection changes
 * @param {function} onTeacherSelected - Callback when teacher is selected
 * @param {function} onCancel - Callback when user cancels selection
 * @param {array} recentlyMessaged - List of recently messaged teacher IDs
 */
const ParentTeacherSelector = ({
  selectedChild,
  onChildChange,
  onTeacherSelected,
  onCancel,
  recentlyMessaged = []
}) => {
  const { user } = useAuth();
  
  // States
  const [children, setChildren] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [error, setError] = useState(null);
  
  // Search and sort
  const [searchTeacher, setSearchTeacher] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filterRecent, setFilterRecent] = useState(false);

  /**
   * Fetch parent's children
   */
  useEffect(() => {
    const fetchChildren = async () => {
      try {
        setLoadingChildren(true);
        setError(null);
        
        const data = await parentService.getChildren();
        // Handle both direct array and wrapped object responses
        const items = Array.isArray(data) ? data : (data.items || []);
        setChildren(items);
        
        // Auto-select first child
        if (items.length > 0 && !selectedChild) {
          const firstChild = items[0];
          onChildChange(firstChild.id);
        }
      } catch (err) {
        setError(`Failed to load children: ${err.message}`);
        console.error('Error fetching children:', err);
      } finally {
        setLoadingChildren(false);
      }
    };
    
    fetchChildren();
  }, [onChildChange, selectedChild]);

  /**
   * Fetch teachers for selected child
   */
  useEffect(() => {
    const fetchTeachers = async () => {
      if (!selectedChild) return;
      
      try {
        setLoadingTeachers(true);
        setError(null);
        setTeachers([]);
        
        const data = await communicationService.getTeachersByStudent(selectedChild);
        const items = data.items || [];
        
        // Normalize and deduplicate teacher data by staff_id
        const seenStaffIds = new Set();
        const normalizedTeachers = items
          .map(teacher => ({
            staff_id: teacher.staff_id || teacher.id,
            first_name: teacher.first_name || 'Unknown',
            last_name: teacher.last_name || 'Teacher',
            email: teacher.email || '',
            subjects: Array.isArray(teacher.subjects) ? teacher.subjects : (teacher.subject ? [teacher.subject] : []),
            subject: Array.isArray(teacher.subjects) ? teacher.subjects.join(', ') : (teacher.subject || ''),
            phone: teacher.phone || ''
          }))
          .filter(teacher => {
            if (seenStaffIds.has(teacher.staff_id)) {
              return false;
            }
            seenStaffIds.add(teacher.staff_id);
            return true;
          });
        
        setTeachers(normalizedTeachers);
        setSelectedTeacher(null);
      } catch (err) {
        if (err.message.includes('404')) {
          setTeachers([]);
        } else {
          setError(`Failed to load teachers: ${err.message}`);
        }
        console.error('Error fetching teachers:', err);
      } finally {
        setLoadingTeachers(false);
      }
    };
    
    fetchTeachers();
  }, [selectedChild]);

  /**
   * Filter and sort teachers with recent/favorite separation
   */
  const filteredTeachers = useMemo(() => {
    let filtered = teachers.filter(teacher => {
      const fullName = `${teacher.first_name || 'Unknown'} ${teacher.last_name || 'Teacher'}`;
      const subjects = Array.isArray(teacher.subjects) 
        ? teacher.subjects.join(' ').toLowerCase()
        : (teacher.subject || '').toLowerCase();
      const searchLower = searchTeacher.toLowerCase();
      
      const matchesSearch = fullName.toLowerCase().includes(searchLower) || 
                            subjects.includes(searchLower);
      
      if (filterRecent) {
        return matchesSearch && recentlyMessaged.includes(teacher.staff_id);
      }
      return matchesSearch;
    });

    // Sort methods
    if (sortBy === 'recent') {
      filtered.sort((a, b) => {
        const aRecent = recentlyMessaged.includes(a.staff_id) ? -1 : 1;
        const bRecent = recentlyMessaged.includes(b.staff_id) ? -1 : 1;
        if (aRecent !== bRecent) return aRecent - bRecent;
        
        const nameA = `${a.first_name || 'Unknown'} ${a.last_name || 'Teacher'}`;
        const nameB = `${b.first_name || 'Unknown'} ${b.last_name || 'Teacher'}`;
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const nameA = `${a.first_name || 'Unknown'} ${a.last_name || 'Teacher'}`;
        const nameB = `${b.first_name || 'Unknown'} ${b.last_name || 'Teacher'}`;
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === 'subject') {
      filtered.sort((a, b) => {
        const aSubjects = a.subject || '';
        const bSubjects = b.subject || '';
        return aSubjects.localeCompare(bSubjects);
      });
    }

    return filtered;
  }, [teachers, searchTeacher, sortBy, recentlyMessaged, filterRecent]);

  /**
   * Separate recently messaged teachers
   */
  const recentlyMessagedTeachers = useMemo(() => {
    return teachers.filter(t => recentlyMessaged.includes(t.staff_id))
      .slice(0, 5);
  }, [teachers, recentlyMessaged]);

  /**
   * Handle teacher confirmation
   */
  const handleConfirm = useCallback(() => {
    if (!selectedTeacher) {
      setError('Please select a teacher');
      return;
    }
    
    const teacherName = `${selectedTeacher.first_name || 'Unknown'} ${selectedTeacher.last_name || 'Teacher'}`;
    onTeacherSelected(selectedTeacher.staff_id, teacherName);
  }, [selectedTeacher, onTeacherSelected]);

  return (
    <div className="selector-overlay">
      <div className="selector-modal">
        {/* Header with Progress */}
        <div className="selector-header">
          <div className="selector-header-content">
            <h2>Start a Conversation</h2>
            <p className="selector-subtitle">Message your child's teachers</p>
          </div>
          <button className="selector-close" onClick={onCancel} aria-label="Close" title="Close (Esc)">
            ✕
          </button>
        </div>

        {/* Step Indicator */}
        <div className="selector-steps">
          <div className={`selector-step ${selectedChild ? 'completed' : 'active'}`}>
            <div className="step-number">1</div>
            <div className="step-label">Select Child</div>
          </div>
          <div className="step-divider"></div>
          <div className={`selector-step ${selectedTeacher ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Choose Teacher</div>
          </div>
        </div>

        <div className="selector-content">
          {/* Child Selection */}
          <div className="selector-section">
            <label className="selector-label">Your Child</label>
            {loadingChildren ? (
              <div className="selector-loading">
                <div className="chat-spinner"></div>
                <p>Loading children...</p>
              </div>
            ) : children.length === 0 ? (
              <div className="selector-empty">No children found</div>
            ) : (
              <select 
                className="selector-input"
                value={selectedChild || ''}
                onChange={(e) => onChildChange(e.target.value)}
              >
                <option value="">Select a child...</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name || `${child.first_name} ${child.last_name}`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Teacher Selection */}
          {selectedChild && (
            <>
              {/* Error Alert */}
              {error && (
                <div className="selector-error">
                  <div className="error-content">
                    <span className="error-icon">⚠</span>
                    <span>{error}</span>
                  </div>
                  <button 
                    className="error-close"
                    onClick={() => setError(null)}
                    aria-label="Close error"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Recently Messaged Section */}
              {recentlyMessagedTeachers.length > 0 && !searchTeacher && (
                <div className="selector-section">
                  <label className="selector-label">Recently Messaged</label>
                  <div className="selector-chips">
                    {recentlyMessagedTeachers.map(teacher => (
                      <button
                        key={teacher.staff_id}
                        className={`selector-chip ${selectedTeacher?.staff_id === teacher.staff_id ? 'selected' : ''}`}
                        onClick={() => setSelectedTeacher(teacher)}
                        title={`${teacher.first_name} ${teacher.last_name}${teacher.subject ? ' - ' + teacher.subject : ''}`}
                      >
                        <span className="chip-avatar">
                          {(teacher.first_name || 'U').charAt(0)}{(teacher.last_name || 'T').charAt(0)}
                        </span>
                        <span className="chip-name">{teacher.first_name} {teacher.last_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search and Controls */}
              <div className="selector-section">
                <label className="selector-label">Teachers</label>
                <div className="selector-search-wrapper">
                  <div className="selector-search-container">
                    <span className="search-icon"><MagnifyingGlass size={16} /></span>
                    <input
                      type="text"
                      className="selector-search"
                      placeholder="Search teachers by name or subject..."
                      value={searchTeacher}
                      onChange={(e) => setSearchTeacher(e.target.value)}
                      autoFocus
                    />
                    {searchTeacher && (
                      <button 
                        className="search-clear"
                        onClick={() => setSearchTeacher('')}
                        aria-label="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="selector-controls">
                    <select 
                      className="selector-sort"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      aria-label="Sort teachers by"
                    >
                      <option value="recent">Recent first</option>
                      <option value="name">Name (A-Z)</option>
                      <option value="subject">Subject</option>
                    </select>

                    {recentlyMessaged.length > 0 && (
                      <button
                        className={`selector-filter-btn ${filterRecent ? 'active' : ''}`}
                        onClick={() => setFilterRecent(!filterRecent)}
                        title="Show only recently messaged teachers"
                      >
                        <Star size={16} /> {recentlyMessaged.length}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Teacher List */}
              <div className="selector-list">
                {loadingTeachers ? (
                  <div className="selector-loading">
                    <div className="chat-spinner"></div>
                    <p>Loading teachers...</p>
                  </div>
                ) : filteredTeachers.length === 0 ? (
                  <div className="selector-empty">
                    {teachers.length === 0 
                      ? 'No teachers found for this child'
                      : searchTeacher 
                      ? 'No matching teachers'
                      : 'No teachers available'}
                  </div>
                ) : (
                  filteredTeachers.map(teacher => {
                    const isRecent = recentlyMessaged.includes(teacher.staff_id);
                    return (
                      <div
                        key={teacher.staff_id}
                        className={`selector-item ${selectedTeacher?.staff_id === teacher.staff_id ? 'selected' : ''} ${isRecent ? 'recent' : ''}`}
                        onClick={() => setSelectedTeacher(teacher)}
                        role="option"
                        aria-selected={selectedTeacher?.staff_id === teacher.staff_id}
                      >
                        {/* Avatar */}
                        <div className="selector-avatar">
                          {(teacher.first_name || 'U').charAt(0)}
                          {(teacher.last_name || 'T').charAt(0)}
                        </div>
                        
                        {/* Info */}
                        <div className="selector-info">
                          <div className="selector-name">
                            {teacher.first_name || 'Unknown'} {teacher.last_name || 'Teacher'}
                            {isRecent && <span className="recent-badge">★</span>}
                          </div>
                          {(teacher.subjects && teacher.subjects.length > 0) && (
                            <div className="selector-subject">
                              {teacher.subjects.join(', ')}
                            </div>
                          )}
                          {teacher.email && (
                            <div className="selector-email">{teacher.email}</div>
                          )}
                        </div>

                        {/* Selection indicator */}
                        {selectedTeacher?.staff_id === teacher.staff_id && (
                          <div className="selector-checkmark">✓</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {selectedChild && (
          <div className="selector-footer">
            <button 
              className="selector-btn-cancel"
              onClick={onCancel}
              aria-label="Cancel and close this dialog"
            >
              Cancel
            </button>
            <button 
              className={`selector-btn-confirm ${selectedTeacher ? 'active' : 'disabled'}`}
              onClick={handleConfirm}
              disabled={!selectedTeacher}
              aria-label={selectedTeacher ? `Message ${selectedTeacher.first_name || 'Unknown'} ${selectedTeacher.last_name || 'Teacher'}` : 'Select a teacher to message'}
            >
              {selectedTeacher ? (
                <>
                  <span className="btn-icon">→</span>
                  <span className="btn-text">Message {selectedTeacher.first_name || 'Unknown'}</span>
                </>
              ) : (
                'Select a teacher'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentTeacherSelector;
